import { APIS, DEFAULT_API } from './api.js'
import { errorLocation } from './buildReport.js'
import { editDistance } from './editDistance.js'
import { explainNaN, explainWarning } from './hints.js'

export const MAX_WARNINGS = 20

const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

// Containment catches radius → roundRadius, which is 5 edits apart.
export const suggestOptions = (option, known) => {
  const lower = option.toLowerCase()
  return known
    .map((name) => ({ name, d: editDistance(lower, name.toLowerCase()) }))
    .filter(({ name, d }) => d <= 3 || name.toLowerCase().includes(lower) || (name.length >= 3 && lower.includes(name.toLowerCase())))
    .sort((a, b) => a.d - b.d)
    .map(({ name }) => name)
}

const TAU = Math.PI * 2
// Float noise lands just over a multiple of 2π (6.284386356666759 read as
// degrees once); values that close to a multiple are radians, not degrees.
const TAU_MARGIN = 0.01
const nearMultipleOfTau = (a) => {
  const r = Math.abs(a) % TAU
  return Math.min(r, TAU - r) <= TAU_MARGIN
}

const kindOf = (value) => (Array.isArray(value) ? 'array' : typeof value === 'number' ? 'number' : null)

const degreeLike = (angle) => [angle].flat().find((a) => typeof a === 'number' && Math.abs(a) > TAU && !nearMultipleOfTau(a))

// Twice the signed area of one closed path of [x, y] points (shoelace), or
// NaN when it is not one.
const doubledArea = (path) => {
  let sum = 0
  for (let i = 0; i < path.length; i += 1) {
    const [x0, y0] = path[i]
    const [x1, y1] = path[(i + 1) % path.length]
    sum += x0 * y1 - x1 * y0
  }
  return sum
}

// The signed area of a flat list of points or a list of paths (holes wound the
// other way subtract), undefined when the argument is not points.
export const outlineArea = (points) => {
  if (!Array.isArray(points) || points.length < 3) return undefined
  const paths = Array.isArray(points[0]?.[0]) ? points : [points]
  try {
    const area = paths.reduce((total, path) => total + doubledArea(path), 0) / 2
    return Number.isFinite(area) ? area : undefined
  } catch {
    return undefined
  }
}

// primitives.polygon's own orientation option flips the points it is given;
// with `paths` the points are not in outline order.
const polygonOptionsArea = ([options]) => {
  if (!isPlainObject(options) || options.paths !== undefined) return undefined
  const area = outlineArea(options.points)
  return options.orientation === 'clockwise' && area !== undefined ? -area : area
}

const pointsArea = ([points]) => (isPlainObject(points) ? polygonOptionsArea([points]) : outlineArea(points))

// Only a single flat outline is reversed: in a list of paths a clockwise one
// may be a hole, and an explicit clockwise orientation is the caller's choice.
const isFlatOutline = (points) => Array.isArray(points) && points.length >= 3 && typeof points[0]?.[0] === 'number'

const reversedPolygonOptions = ([options, ...rest]) =>
  isPlainObject(options) && options.paths === undefined && options.orientation !== 'clockwise' && isFlatOutline(options.points)
    ? [{ ...options, points: [...options.points].reverse() }, ...rest]
    : undefined

const reversedPoints = ([points, ...rest]) => (isFlatOutline(points) ? [[...points].reverse(), ...rest] : undefined)

// Fluent's cylinder reads outer only for a hollow one, so alone it leaves radius 1.
const loneOuter = ([options]) =>
  isPlainObject(options) && options.outer !== undefined && options.inner === undefined && options.wall === undefined
    ? { option: 'outer', hint: 'outer takes effect only with inner or wall; use radius for a solid cylinder' }
    : undefined

const isNumbers = (value, n) => Array.isArray(value) && value.length >= n && value.slice(0, n).every(Number.isFinite)

// Modeling's own defaults, which a call that leaves the size out gets.
const ROUNDED_DEFAULTS = { roundedCuboid: { size: [2, 2, 2] }, roundedRectangle: { size: [2, 2] }, roundedCylinder: { height: 2, radius: 1 } }

// The largest roundRadius a rounded primitive takes, and the size that sets it.
const roundRadiusLimit = (kind, options) => {
  const o = { ...ROUNDED_DEFAULTS[kind], ...options }
  if (kind === 'roundedCylinder') {
    if (!Number.isFinite(o.height) || !Number.isFinite(o.radius)) return undefined
    return o.height / 2 < o.radius ? { limit: o.height / 2, height: o.height } : { limit: o.radius, radius: o.radius }
  }
  const n = kind === 'roundedCuboid' ? 3 : 2
  if (!isNumbers(o.size, n)) return undefined
  const least = Math.min(...o.size.slice(0, n))
  return { limit: least / 2, least }
}

// Modeling refuses a roundRadius within its EPS (1e-5) of the limit, so a
// radius past that becomes the largest one in thousandths it takes.
const LIMIT_EPS = 1e-5
const clampRoundRadius = (kind) => ([options, ...rest]) => {
  if (!isPlainObject(options) || !Number.isFinite(options.roundRadius) || options.roundRadius <= 0) return undefined
  const found = roundRadiusLimit(kind, options)
  if (!found || found.limit <= 0 || options.roundRadius <= found.limit - LIMIT_EPS) return undefined
  const { limit, ...rule } = found
  const to = Math.max(0, Math.floor((limit - LIMIT_EPS) * 1000) / 1000)
  return { args: [{ ...options, roundRadius: to }, ...rest], facts: [{ option: 'roundRadius', clamped: true, from: options.roundRadius, to, ...rule }] }
}

// cylinderElliptic takes each end's radii as an [x, y] pair.
const pairRadii = ([options, ...rest]) => {
  if (!isPlainObject(options)) return undefined
  const fixed = { ...options }
  const facts = []
  for (const option of ['startRadius', 'endRadius']) {
    const r = options[option]
    if (!Number.isFinite(r)) continue
    fixed[option] = [r, r]
    facts.push({ option, coerced: true, from: r, to: [r, r] })
  }
  return facts.length ? { args: [fixed, ...rest], facts } : undefined
}

// jf.polygon takes the points array; given modeling's { points } it passes
// the object on as the points, which no engine reads.
const unwrapPoints = ([options, ...rest]) =>
  isPlainObject(options) && Array.isArray(options.points) && Object.keys(options).length === 1
    ? { args: [options.points, ...rest], facts: [{ option: 'points', unwrapped: true }] }
    : undefined

const fixSpecs = (prefix) => ({
  [`${prefix}roundedCuboid`]: { fix: clampRoundRadius('roundedCuboid') },
  [`${prefix}roundedRectangle`]: { fix: clampRoundRadius('roundedRectangle') },
  [`${prefix}roundedCylinder`]: { fix: clampRoundRadius('roundedCylinder') },
  [`${prefix}cylinderElliptic`]: { fix: pairRadii },
})

// A 2D shape has no depth, so a turn about X or Y other than a mirror (a
// multiple of π) squashes it into its shadow in the XY plane.
const nearMultipleOfPi = (a) => {
  const r = Math.abs(a) % Math.PI
  return Math.min(r, Math.PI - r) <= 1e-9
}
const tilts = (angle) => Number.isFinite(angle) && !nearMultipleOfPi(angle)
const tilted = (axis, angle) => (axis === 'xyz' ? Array.isArray(angle) && angle.slice(0, 2).some(tilts) : tilts(angle))

const isGeom2 = (shape) =>
  shape !== null &&
  typeof shape === 'object' &&
  (shape.isManifoldGeom2 === true || (shape.isTreeNode === true && shape.dim === 2) || 'sides' in shape || 'outlines' in shape)

const TILTS = { rotateX: 'x', rotateY: 'y', rotate: 'xyz' }
const tiltSpecs = (prefix, method) => Object.fromEntries(Object.entries(TILTS).map(([name, axis]) => [`${prefix}${name}`, { tilt: axis, ...(method ? { method } : {}) }]))

const BOOLEANS = ['union', 'subtract', 'intersect']
const booleanSpecs = (prefix) => Object.fromEntries(BOOLEANS.map((op) => [`${prefix}${op}`, { boolean: op }]))

// Checks the generated option table cannot describe, keyed by the table's
// prefix ('' is @jscad/modeling, 'jf.' jscad-fluent): where a 2D outline
// enters as points, the booleans, and the options fixed before the call.
const EXTRA_SPECS = {
  '': {
    'primitives.polygon': { outline: polygonOptionsArea, reverse: reversedPolygonOptions },
    'geometries.geom2.fromPoints': { outline: pointsArea, reverse: reversedPoints },
    ...booleanSpecs('booleans.'),
    ...fixSpecs('primitives.'),
    ...tiltSpecs('transforms.'),
  },
  'jf.': { polygon: { fix: unwrapPoints, outline: pointsArea, reverse: reversedPoints }, cylinder: { ignored: loneOuter }, ...booleanSpecs(''), ...fixSpecs('') },
}
const EXTRA_METHOD_SPECS = {
  FluentGeom3: Object.fromEntries(BOOLEANS.map((op) => [op, { boolean: op, method: true }])),
  FluentGeom2: { ...Object.fromEntries(BOOLEANS.map((op) => [op, { boolean: op, method: true }])), ...tiltSpecs('', true) },
}

// Manifold shapes convert their polygons lazily, so ask them first.
const isEmptyShape = (shape) => {
  if (shape === null || typeof shape !== 'object') return false
  if (typeof shape.isEmpty === 'function') return shape.isEmpty() === true
  if (Array.isArray(shape.polygons)) return shape.polygons.length === 0
  if (Array.isArray(shape.sides)) return shape.sides.length === 0
  return false
}

// jf.hullPoints3 and a hand-made mesh give { points, faces }, which only a
// polyhedron factory turns into a shape.
const isMeshData = (value) =>
  value !== null && typeof value === 'object' && Array.isArray(value.points) && Array.isArray(value.faces) && !('polygons' in value)

const operandsOf = (spec, self, args) => (spec.method ? [self, ...args] : args).flat(Infinity)

// A mistyped option or a NaN size often makes the function throw a message
// that names neither; the error then carries the hint too.
const annotate = (error, hints) => {
  try {
    if (typeof error?.message !== 'string') return
    const added = [...new Set(hints)].filter((hint) => hint && !error.message.includes(hint))
    if (added.length) error.message = `${error.message}\n${added.join('\n')}`
  } catch {
    // A frozen error goes out as it came.
  }
}

// The argument positions that may hold an options object: the first, the
// index `optionsAt` names (attachTo/alignTo take options after their anchors),
// or `'last'` for a trailing options object in a variadic list (subtract's).
const optionIndices = (spec, args) => {
  const at = spec.optionsAt
  if (at === 'last') return args.length ? [args.length - 1] : []
  if (typeof at === 'number') return [at]
  return [0]
}

/**
 * @param {string} fnName
 * @param {Function} fn
 * @param {{ known?: string[], types?: Record<string, string>, angle?: boolean, outline?: Function, reverse?: Function, ignored?: Function, boolean?: string, method?: boolean, optionsAt?: number | 'last' }} spec
 * @param {(fact: object) => ({ hint?: string } | void)} warn
 */
const checked = (fnName, fn, spec, warn) => {
  const allowed = spec.known && new Set(spec.known)
  return function (...given) {
    let args = given
    const hints = []
    const report = (fact) => {
      // The stack names the model's call site for the collector; kept off the
      // fact's own keys, which stay the plain data a warning is made of.
      const warning = warn(Object.defineProperty({ ...fact }, 'stack', { value: new Error().stack }))
      if (warning?.hint) hints.push(warning.hint)
    }
    // A hostile or revoked options argument, or a throwing warn, must never
    // stop the wrapped call: warnings are reported, never thrown.
    try {
      // A slip with one sensible reading (a roundRadius past its limit, a
      // number radius where a pair is taken) is fixed, with a warning.
      const fixed = spec.fix?.(args)
      if (fixed) {
        args = fixed.args
        for (const fact of fixed.facts) report({ fn: fnName, ...fact })
      }
      const [first] = args
      for (const i of optionIndices(spec, args)) {
        const object = args[i]
        if ((allowed || spec.types) && isPlainObject(object)) {
          for (const option of Object.keys(object)) {
            if (allowed && !allowed.has(option)) {
              report({ fn: fnName, option, suggestions: suggestOptions(option, spec.known) })
              continue
            }
            const expected = spec.types?.[option]
            const got = kindOf(object[option])
            if (expected && got && got !== expected) report({ fn: fnName, option, expected, got })
          }
        }
      }
      if (spec.angle) {
        const value = degreeLike(first)
        if (value !== undefined) report({ fn: fnName, option: 'angle', value })
      }
      if (spec.outline) {
        const area = spec.outline(args)
        // An inside-out extrusion is never what a request wants.
        const reversed = area < 0 ? spec.reverse?.(args) : undefined
        if (reversed) args = reversed
        if (area < 0) report({ fn: fnName, option: 'points', area, ...(reversed ? { reversed: true } : {}) })
      }
      if (spec.tilt && tilted(spec.tilt, first) && (spec.method || args.slice(1).flat(Infinity).some(isGeom2))) {
        report({ fn: fnName, option: 'angle', flat: true })
      }
      const ignored = spec.ignored?.(args)
      if (ignored) report({ fn: fnName, ...ignored, ignored: true })
      if (spec.boolean && operandsOf(spec, this, args).some(isMeshData)) report({ fn: fnName, meshOperand: true })
    } catch {
      // Ignored — the original call below still runs.
    }
    let result
    try {
      result = fn.apply(this, args)
    } catch (error) {
      annotate(error, [...hints, explainNaN(args[0], spec.types)])
      throw error
    }
    // A deferred call cannot throw for a NaN option now; evaluation would
    // fail later without naming it. Fail fast with the same hint a sync
    // throw gets. Eager results are never tree nodes, so they keep their
    // engine's own error.
    if (result?.isTreeNode === true) {
      const nanHint = explainNaN(args[0], spec.types)
      if (nanHint) {
        const error = new Error(`${fnName}: ${nanHint}`)
        annotate(error, hints)
        throw error
      }
    }
    try {
      if (spec.boolean && spec.boolean !== 'union' && isEmptyShape(result) && !isEmptyShape(operandsOf(spec, this, args)[0])) {
        report({ fn: fnName, empty: spec.boolean })
      }
    } catch {
      // A shape that cannot be inspected goes out unchecked.
    }
    return result
  }
}

const specsOf = (table) => {
  /** @type {Map<string, { known?: string[], types?: Record<string, string>, angle?: boolean }>} */
  const specs = new Map()
  const spec = (path) => {
    if (!specs.has(path)) specs.set(path, {})
    return specs.get(path)
  }
  for (const [path, known] of Object.entries(table.options ?? {})) spec(path).known = known
  for (const [path, types] of Object.entries(table.types ?? {})) spec(path).types = types
  for (const path of table.angles ?? []) spec(path).angle = true
  for (const [path, at] of Object.entries(table.optionsAt ?? {})) spec(path).optionsAt = at
  for (const [path, extra] of Object.entries(EXTRA_SPECS[table.prefix] ?? {})) Object.assign(spec(path), extra)
  return specs
}

const methodSpecsOf = (table) => {
  /** @type {Map<string, Map<string, { known?: string[], types?: Record<string, string>, angle?: boolean }>>} */
  const classes = new Map()
  const spec = (cls, name) => {
    if (!classes.has(cls)) classes.set(cls, new Map())
    const methods = classes.get(cls)
    if (!methods.has(name)) methods.set(name, {})
    return methods.get(name)
  }
  for (const [cls, methods] of Object.entries(table.methods ?? {})) {
    for (const [name, known] of Object.entries(methods)) spec(cls, name).known = known
  }
  for (const [cls, methods] of Object.entries(table.methodTypes ?? {})) {
    for (const [name, types] of Object.entries(methods)) spec(cls, name).types = types
  }
  for (const [cls, names] of Object.entries(table.methodAngles ?? {})) {
    for (const name of names) spec(cls, name).angle = true
  }
  for (const [cls, methods] of Object.entries(table.methodOptionsAt ?? {})) {
    for (const [name, at] of Object.entries(methods)) spec(cls, name).optionsAt = at
  }
  for (const [cls, methods] of Object.entries(EXTRA_METHOD_SPECS)) {
    for (const [name, extra] of Object.entries(methods)) Object.assign(spec(cls, name), extra)
  }
  return classes
}

// esbuild's CJS namespaces export non-configurable getters, so the copy
// re-declares every property configurable before any is replaced.
const copyOf = (node) => {
  const copy = Object.create(Object.getPrototypeOf(node))
  for (const key of Reflect.ownKeys(node)) {
    Object.defineProperty(copy, key, { ...Object.getOwnPropertyDescriptor(node, key), configurable: true })
  }
  return copy
}

const setValue = (target, key, value) =>
  Object.defineProperty(target, key, { value, enumerable: true, writable: true, configurable: true })

export const withOptionChecks = (api, table, warn) => {
  if (!table || api === null || typeof api !== 'object') return api
  const copies = new Map([[api, copyOf(api)]])
  const root = copies.get(api)
  for (const [path, spec] of specsOf(table)) {
    const keys = path.split('.')
    const last = keys.pop()
    const target = keys.reduce((node, key) => (node?.[key] !== null && typeof node?.[key] === 'object' ? node[key] : null), api)
    // A namespace is copied only on the way to a function it has.
    if (typeof target?.[last] !== 'function') continue
    let original = api
    let copy = root
    for (const key of keys) {
      const child = original[key]
      if (!copies.has(child)) copies.set(child, copyOf(child))
      setValue(copy, key, copies.get(child))
      original = child
      copy = copies.get(child)
    }
    const wrapped = checked(table.prefix + path, target[last], spec, warn)
    setValue(copy, last, wrapped)
    // Manifold also re-exports namespaced functions at the top level as the
    // same function object; keep both bindings pointing at one wrapper.
    for (const key of Object.keys(api)) {
      if (api[key] === target[last]) setValue(root, key, wrapped)
    }
  }
  if (api.default === api) setValue(root, 'default', root)
  return root
}

const FLUENT_FACTORIES = {
  FluentGeom2: 'circle',
  FluentGeom3: 'cube',
  FluentPath2: 'arc',
  FluentGeom2Array: 'geom2Array',
  FluentGeom3Array: 'geom3Array',
  FluentPath2Array: 'path2Array',
}

// Fluent exports FluentGeom2 and FluentGeom3 but not the *Array classes, so
// every prototype comes from an object a factory makes.
export const fluentPrototypes = (jf) => {
  /** @type {Record<string, object>} */
  const protos = {}
  for (const [cls, factory] of Object.entries(FLUENT_FACTORIES)) {
    if (typeof jf?.[factory] !== 'function') continue
    try {
      const made = jf[factory]()
      if (made !== null && typeof made === 'object') protos[cls] = Object.getPrototypeOf(made)
    } catch {
      // An engine without this primitive leaves the class unchecked.
    }
  }
  const array = protos.FluentGeom2Array ?? protos.FluentGeom3Array ?? protos.FluentPath2Array
  if (array) protos.FluentGeometryArray = Object.getPrototypeOf(array)
  return protos
}

// The prototypes outlive any one copy of this module (another bundle, a test
// file's fresh import), so the mark and the warn target are global.
const WRAPPED = Symbol.for('jscadui.optionChecks.wrapped')
const METHOD_WARN = Symbol.for('jscadui.optionChecks.methodWarn')

export const setMethodWarn = (warn) => {
  globalThis[METHOD_WARN] = warn
}

const reportMethod = (warning) => globalThis[METHOD_WARN]?.(warning)

export const wrapFluentMethods = (jf, table, warn) => {
  setMethodWarn(warn)
  if (!table || jf === null || typeof jf !== 'object') return
  const protos = fluentPrototypes(jf)
  for (const [cls, methods] of methodSpecsOf(table)) {
    const proto = protos[cls]
    if (!proto) continue
    for (const [name, spec] of methods) {
      const descriptor = Object.getOwnPropertyDescriptor(proto, name)
      if (typeof descriptor?.value !== 'function' || descriptor.value[WRAPPED]) continue
      const wrapped = checked(`${cls}.${name}`, descriptor.value, spec, reportMethod)
      wrapped[WRAPPED] = true
      Object.defineProperty(proto, name, { ...descriptor, value: wrapped })
    }
  }
}

// The collector knows the chat's API style, so it turns each fact the checks
// report into the warning the model sees; warn returns it for the thrown-error hint.
// With `base`, the project URL model code runs under, each warning names the
// file and line of its call, and each call site of a slip is listed once.
export const createWarningCollector = ({ cap = MAX_WARNINGS, base } = {}) => {
  let seen = new Set()
  let list = []
  let api = DEFAULT_API
  return {
    warn: ({ stack, ...fact }) => {
      const { file, line } = base && typeof stack === 'string' ? errorLocation({ stack }, base) : {}
      const warning = { ...explainWarning(fact, api), ...(file ? { file, line } : {}) }
      const key = [warning.fn, warning.option, file, line].join('\u0000')
      if (seen.has(key) || list.length >= cap) return warning
      seen.add(key)
      list.push(warning)
      return warning
    },
    setApi: (next) => {
      api = APIS.includes(next) ? next : DEFAULT_API
    },
    getApi: () => api,
    reset: () => {
      seen = new Set()
      list = []
    },
    list: () => [...list],
  }
}
